/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import { createAction } from '@reduxjs/toolkit';
import { Coordinates, SetEntityPropertyParams } from 'app';

import { BoardHurdle } from './hurdleTypes';
import { CameraMode, Horse } from './reducer';

/**
 * Animation intent for horse movement: explicit path and profile for deterministic animation.
 * When present, the reducer and Horse component use this instead of inferring from move/xy.
 */
export type AnimationIntentMode = 'live' | 'history' | 'teleport';

/** Global monotonic counter for animation sequence IDs. */
let nextSeqIdCounter = 1;
export function getNextAnimationSequenceId(): number {
  return nextSeqIdCounter++;
}

export interface AnimationIntent {
  mode: AnimationIntentMode;
  speedProfile: string;
  sequenceId: number;
  /** Path points (start ... end). Must have at least one point. */
  path: Coordinates[];
}

/**
 * Set hurdles on the board with progress metadata and precomputed tactical envelopes.
 */
export const setHurdles = createAction<BoardHurdle[]>('setHurdles');

/**
 * Set coordinates of all possible moves the current horse could select. A move represented by an array
 * of {@link Coordinates}, so all moves are represented in a 2-dimensional array.
 */
export const setMoves = createAction<Coordinates[][]>('setMoves');

/**
 * Sets basic information about all horses on the board with an array of type {@link Horse}.
 */
export const setHorses = createAction<Horse[]>('setHorses');

/**
 * Sets basic information about a specific horse on the board type {@link Horse}.
 */
export const setHorse = createAction<Horse>('setHorse');

export interface HorseField extends Coordinates {
  move?: Coordinates[];
  pos: number;
  prog: number;
  /**
   * Optional explicit animation intent. When set, animation uses this path and profile;
   * otherwise falls back to move/static position (backward compatible).
   */
  animationIntent?: AnimationIntent;
}

/**
 * Sets location of all horses on the board with an array of type {@link Coordinates}.
 */
export const setHorsesField = createAction<HorseField[]>('setHorsesField');

/**
 * Sets location a specific hors on the board with type {@link Coordinates}.
 */
export const setHorseField = createAction<SetEntityPropertyParams<HorseField>>('setHorseField');

export interface MoveCameraPayload {
  readonly x: number;
  readonly y: number;
  readonly scale: number;
}
export const moveCamera = createAction<MoveCameraPayload>('moveCamera');

export const setCameraMode = createAction<CameraMode>('setCameraMode');

/**
 * Set follow-mode zoom preference in **stops** (exposure-style; see cameraZoom.ts).
 * Persisted. Replaces legacy multiplier factor.
 */
export const setFollowZoomStops = createAction<number>('setFollowZoomStops');

/** Request initial follow-camera recompute (e.g. when board mounts). Ensures first frame uses a deterministic camera path. */
export const requestInitialCamera = createAction<void>('requestInitialCamera');
